import React, { useEffect, useRef, useState } from "react";
import {
  getSubDepartments,
  getTeamDetails,
  getTeamsByDepartment,
  TeamMember,
  getParentDepartmentOptions
} from "./api/organization.api";
import UserAvatar from "../../ui/icons/UserAvatar";
import { useTeam } from "../../../features/TeamcompositeResults/contexts/TeamContext";
import { useNavigate } from "react-router-dom";
import { SidebarSelect, SidebarSelectOption } from "../SidebarSelect";

interface Option {
  id: number;
  name: string;
}
interface TreeNode {
  id: number;
  name: string;
  children?: TreeNode[];
}

export type DeptTeamSelectorMode = "org" | "dept_lead" | "user";

interface DeptTeamSelectorProps {
  organizationId: number;
  onTeamChange?: (team: Option, isUserAction?: boolean) => void;
  mode: DeptTeamSelectorMode;
  allowedDepartments?: Option[];
  userTeams?: Option[];
  isTeamLead?: boolean;
  teamsLeading?: Option[];
}

const DeptTeamSelector: React.FC<DeptTeamSelectorProps> = React.memo(
  ({
    organizationId,
    onTeamChange,
    mode,
    allowedDepartments = [],
    userTeams = [],
    teamsLeading = [],
    isTeamLead = false,
  }) => {
    const [departments, setDepartments] = useState<Option[]>([]);
    const [teams, setTeams] = useState<Option[]>([]);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

    const [activeDept, setActiveDept] = useState<Option | null>(null);
    const [activeTeam, setActiveTeam] = useState<Option | null>(null);

    const [deptOpen, setDeptOpen] = useState(false);
    const [teamOpen, setTeamOpen] = useState(false);

    const [leadingTeams, setLeadingTeams] = useState<Option[]>([]);
    const [activeLeadingTeam, setActiveLeadingTeam] = useState<Option | null>(null);
    const [leadingOpen, setLeadingOpen] = useState(false);

    const [deptLoading, setDeptLoading] = useState(false);
    const [teamLoading, setTeamLoading] = useState(false);
    const [membersLoading, setMembersLoading] = useState(false);
    const [deptTree, setDeptTree] = useState<TreeNode[]>([]);
    const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
    const [activeNode, setActiveNode] = useState<TreeNode | null>(null);
    const [treeLoading, setTreeLoading] = useState(false);

    const { setSelectedDepartment, setSelectedTeam } = useTeam();
    const navigate = useNavigate();
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!deptOpen && !teamOpen && !leadingOpen) return;
      const close = () => {
        setDeptOpen(false);
        setTeamOpen(false);
        setLeadingOpen(false);
      };
      const onDocMouse = (e: MouseEvent) => {
        if (!rootRef.current?.contains(e.target as Node)) close();
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") close();
      };
      document.addEventListener("mousedown", onDocMouse);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onDocMouse);
        document.removeEventListener("keydown", onKey);
      };
    }, [deptOpen, teamOpen, leadingOpen]);

    // ORG mode: fetch all departments
    useEffect(() => {
      if (mode !== "org" || !organizationId) return;
      const fetchDepts = async () => {
        setDeptLoading(true);
        try {
          const res = await getParentDepartmentOptions({
            organization_id: organizationId,
            limit: 100,
          });
          if (res?.success && res.data?.rows?.length) {
            const mapped = res.data.rows.map((item: any) => ({
              id: Number(item.value),
              name: item.label,
            }));
            setDepartments(mapped);
            setActiveDept(mapped[0]);
            setSelectedDepartment(mapped[0]);
            onTeamChange?.(mapped[0], false);
          }
        } catch (err) {
          console.warn("Failed to fetch departments", err);
        } finally {
          setDeptLoading(false);
        }
      };
      fetchDepts();
    }, [mode, organizationId, setSelectedDepartment, onTeamChange]);

    useEffect(() => {
      if (teamsLeading.length > 0) {
        const firstTeam = teamsLeading[0];

        setLeadingTeams(teamsLeading);
        setActiveLeadingTeam(firstTeam);

        setActiveTeam(firstTeam);

        setSelectedTeam(firstTeam);
        onTeamChange?.(firstTeam, false);
      }
    }, [teamsLeading, onTeamChange, setSelectedTeam]);

    const handleSelectLeadingTeam = (team: Option) => {
      setActiveLeadingTeam(team);
      setActiveTeam(team);
      setLeadingOpen(false);
      setSelectedTeam(team);
      onTeamChange?.(team, true);
    };

    // DEPT_LEAD mode: use allowedDepartments from login response
    useEffect(() => {
      if (mode !== "dept_lead") return;
      if (allowedDepartments.length > 0) {
        setDepartments(allowedDepartments);
        setActiveDept(allowedDepartments[0]);
      }
    }, [mode, allowedDepartments]);

    // USER mode: use userTeams from login response
    useEffect(() => {
      if (mode === "user" || !activeDept) return;

      const fetchTeamsAndMembers = async () => {
        setTeamLoading(true);

        setTeams([]);
        setActiveTeam(null);
        setTeamMembers([]);

        try {
          const deptIdToUse = activeNode?.id ?? activeDept.id;

          const res = await getTeamsByDepartment(deptIdToUse);

          if (res?.success && res.data?.rows?.length) {
            const mapped = res.data.rows.map((item: any) => ({
              id: Number(item.value),
              name: item.label,
            }));

            setTeams(mapped);

            const firstTeam = mapped[0];

            setActiveTeam(firstTeam);

            setSelectedTeam(firstTeam);
            onTeamChange?.(firstTeam, false);

            try {
              const resMembers = await getTeamDetails(firstTeam.id);

              if (resMembers?.success && resMembers.data) {
                setTeamMembers(resMembers.data.members ?? []);
              }
            } catch (err) {
              console.warn("Failed to fetch team members", err);
            }
          }
        } catch (err) {
          console.warn("Failed to fetch teams", err);
        } finally {
          setTeamLoading(false);
        }
      };

      fetchTeamsAndMembers();
    }, [activeDept, activeNode, mode, onTeamChange, setSelectedTeam]);

    // ── Fetch teams when active department changes (org + dept_lead)

    useEffect(() => {
      if (mode === "user" || !activeDept) return;

      const fetchTeams = async () => {
        setTeamLoading(true);
        setTeams([]);
        setActiveTeam(null);
        setTeamMembers([]);

        try {
          const deptIdToUse = activeNode?.id ?? activeDept.id;

          const res = await getTeamsByDepartment(deptIdToUse);

          if (res?.success && res.data?.rows?.length) {
            const mapped = res.data.rows.map((item: any) => ({
              id: Number(item.value),
              name: item.label,
            }));

            setTeams(mapped);

            const firstTeam = mapped[0];

            setActiveTeam(firstTeam);

            setSelectedTeam(firstTeam);
            onTeamChange?.(firstTeam, false);
          }
        } catch (err) {
          console.warn("Failed to fetch teams", err);
        } finally {
          setTeamLoading(false);
        }
      };

      fetchTeams();
    }, [activeDept, activeNode, mode, onTeamChange, setSelectedTeam]);

    // ── Fetch team members when activeTeam changes
    useEffect(() => {
      if (!activeTeam) return;
      const fetchMembers = async () => {
        setMembersLoading(true);
        try {
          const res = await getTeamDetails(activeTeam.id);
          if (res?.success && res.data) {
            setTeamMembers(res.data.members ?? []);
          }
        } catch (err) {
          console.warn("Failed to fetch team details", err);
        } finally {
          setMembersLoading(false);
        }
      };
      fetchMembers();
    }, [activeTeam]);

    // Handlers
    const handleSelectDept = async (dept: Option) => {
      setActiveDept(dept);
      setSelectedDepartment(dept);

      setDeptTree([]);
      setActiveNode(null);
      setExpandedNodes(new Set());
      setTeams([]);
      setActiveTeam(null);
      setTeamMembers([]);

      try {
        setTreeLoading(true);

        const res = await getSubDepartments({
          organization_id: organizationId,
          department_id: dept.id,
        });

        if (res?.success && res.data?.rows?.length) {
          const mapped = buildTree(res.data.rows, dept.id);
          setDeptTree(mapped);
        } else {
          setDeptTree([]);
        }
      } catch (err) {
        console.warn("Failed to fetch sub departments", err);
      } finally {
        setTreeLoading(false);
      }
    };
    const handleSelectTeam = (team: Option) => {
      setActiveTeam(team);
      setTeamOpen(false);
      setSelectedTeam(team);
      onTeamChange?.(team, true);
    };

    const buildTree = (items: any[], parentId: number): TreeNode[] => {
      return items
        .filter((item) => item.parent?.id === parentId)
        .map((item) => ({
          id: item.id,
          name: item.name,
          children: buildTree(items, item.id), // ✅ recursion
        }));
    };

    const canNavigateToUsers = !!activeTeam;

    type TreeNodeItemProps = {
      node: TreeNode;
      level?: number;
      expandedNodes: Set<number>;
      setExpandedNodes: React.Dispatch<React.SetStateAction<Set<number>>>;
      activeNode: TreeNode | null;
      setActiveNode: React.Dispatch<React.SetStateAction<TreeNode | null>>;
    };

    const TreeNodeItem: React.FC<TreeNodeItemProps> = ({
      node,
      level = 0,
      expandedNodes,
      setExpandedNodes,
      activeNode,
      setActiveNode,
    }) => {
      const isOpen = expandedNodes.has(node.id);
      const hasChildren = !!node.children?.length;

      const toggleNode = () => {
        setExpandedNodes((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(node.id)) newSet.delete(node.id);
          else newSet.add(node.id);
          return newSet;
        });
      };

      return (
        <div
          className="select-none"
          style={{ paddingLeft: `${level * 16}px` }}
        >
          <div
            className={`group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-all duration-150
              ${activeNode?.id === node.id
                ? "bg-gray-200 text-black font-medium"
                : "hover:bg-gray-100 text-gray-800"
              }`}
            onClick={() => {
              setActiveNode(node);
              if (hasChildren) toggleNode();
            }}
          >
            <div className="flex items-center gap-2">
              <span
                className={`w-1.5 h-1.5 rounded-full ${activeNode?.id === node.id
                  ? "bg-blue-500"
                  : "bg-gray-400"
                  }`}
              />

              <span className="text-sm">{node.name}</span>
            </div>
          </div>

          <div
            className={`overflow-hidden transition-all duration-200 ${isOpen ? "max-h-96 mt-1" : "max-h-0"
              }`}
          >
            {hasChildren &&
              node.children!.map((child) => (
                <TreeNodeItem
                  key={child.id}
                  node={child}
                  level={level + 1}
                  expandedNodes={expandedNodes}
                  setExpandedNodes={setExpandedNodes}
                  activeNode={activeNode}
                  setActiveNode={setActiveNode}
                />
              ))}
          </div>
        </div>
      );
    };

    if (deptLoading) {
      return (
        <div className="px-4 space-y-3" aria-busy="true" aria-label="Loading">
          <div>
            <div className="h-3 w-28 bg-white/10 rounded mb-2 animate-pulse" />
            <div className="h-11 w-full bg-white/10 rounded-xl animate-pulse border border-white/5" />
          </div>
        </div>
      );
    }

    const showTeamRow = !(mode === "user" && !isTeamLead);



    return (
      <div ref={rootRef} className="px-4 space-y-1">
        {leadingTeams.length > 0 && (
          <SidebarSelect
            label="Teams Leading"
            valueLabel={activeLeadingTeam?.name ?? ""}
            placeholder="Select team"
            isOpen={leadingOpen}
            onToggle={() => {
              setLeadingOpen((v) => !v);
              setDeptOpen(false);
              setTeamOpen(false);
            }}
          >
            {leadingTeams.map((team) => (
              <SidebarSelectOption
                key={team.id}
                selected={activeLeadingTeam?.id === team.id}
                onSelect={() => handleSelectLeadingTeam(team)}
              >
                {team.name}
              </SidebarSelectOption>
            ))}
          </SidebarSelect>
        )}
        {mode !== "user" && departments.length > 0 && (
          <SidebarSelect
            label="Department"
            valueLabel={activeNode?.name ?? activeDept?.name ?? ""}
            placeholder="Select department"
            isOpen={deptOpen}
            onToggle={() => {
              setDeptOpen((v) => !v);
              setTeamOpen(false);
            }}
          >
            <>
              {departments.map((dept) => (
                <div key={dept.id}>
                  <SidebarSelectOption
                    selected={activeDept?.id === dept.id}
                    onSelect={() => handleSelectDept(dept)}
                  >
                    {dept.name}
                  </SidebarSelectOption>

                  {activeDept?.id === dept.id && (
                    <div className="ml-2 mt-1 border-l border-white/10 pl-2">
                      {treeLoading ? (
                        <div className="text-xs text-white/50 px-2 py-1">
                          Loading...
                        </div>
                      ) : (
                        deptTree.map((node) => (
                          <TreeNodeItem
                            key={node.id}
                            node={node}
                            expandedNodes={expandedNodes}
                            setExpandedNodes={setExpandedNodes}
                            activeNode={activeNode}
                            setActiveNode={setActiveNode}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          </SidebarSelect>
        )}

        {showTeamRow &&
          (teamLoading ? (
            <SidebarSelect
              label="Team"
              valueLabel=""
              placeholder=""
              isOpen={false}
              onToggle={() => { }}
              busy
              disabled
            >
              {null}
            </SidebarSelect>
          ) : teams.length > 1 ? (
            <SidebarSelect
              label="Team"
              valueLabel={activeTeam?.name ?? ""}
              placeholder="Select team"
              isOpen={teamOpen}
              onToggle={() => {
                setTeamOpen((v) => !v);
                setDeptOpen(false);
              }}
            >
              {teams.map((team) => (
                <SidebarSelectOption
                  key={team.id}
                  selected={activeTeam?.id === team.id}
                  onSelect={() => handleSelectTeam(team)}
                >
                  {team.name}
                </SidebarSelectOption>
              ))}
            </SidebarSelect>
          ) : teams.length === 1 ? (
            <div className="mb-3">
              <span className="block text-[11px] font-primary font-semibold uppercase tracking-[0.06em] text-white/55 mb-1.5">
                Team
              </span>
              <div className="rounded-xl bg-white/10 border border-white/20 px-3 py-2.5 min-h-[44px] flex items-center">
                <span className="text-[15px] font-medium text-white font-primary truncate">
                  {teams[0].name}
                </span>
              </div>
            </div>
          ) : mode !== "user" && departments.length > 0 ? (
            <div className="mb-3 rounded-xl border border-dashed border-white/25 bg-white/5 px-3 py-3 text-center">
              <p className="text-white/55 text-[13px] font-primary m-0 leading-snug">
                No teams in this department yet.
              </p>
            </div>
          ) : null)}

        {mode === "user" && !isTeamLead ? null : membersLoading ? (
          <div className="text-white/50 text-sm font-primary animate-pulse mt-6">
            Loading members…
          </div>
        ) : teamMembers.length > 0 ? (
          <div
            className={`mt-6 flex items-center flex-wrap ${canNavigateToUsers
              ? "cursor-pointer"
              : "cursor-not-allowed opacity-60"
              }`}
            onClick={() => {
              if (canNavigateToUsers) {
                navigate("/user-management");
              }
            }}
          >
            {teamMembers.slice(0, 3).map((member, i) => (
              <div
                key={member.id}
                className="relative group -mr-[11px] cursor-pointer"
                style={{ zIndex: teamMembers.length - i }}
              >
                <UserAvatar
                  src={member.image ?? ""}
                  className="w-[44px] h-[44px] border-[3px] border-dark-blue"
                />

                <div
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white text-dark-blue text-xs font-primary font-semibold px-2 py-1 rounded-md 
                  shadow-[0_4px_12px_rgba(0,0,0,0.15)] whitespace-nowrap pointer-events-none opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0
                  transition-all duration-200 ease-out z-50"
                >
                  {member.first_name} {member.last_name}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-white" />
                </div>
              </div>
            ))}

            {/* +N bubble — also with tooltip */}
            {teamMembers.length > 3 && (
              <div
                className="relative group flex items-center justify-center rounded-full w-[44px] h-[44px] bg-[#5d4cc6] flex-shrink-0 -mr-[11px] text-white text-sm font-semibold border-[3px] border-dark-blue cursor-default"
                style={{ zIndex: 0 }}
              >
                +{teamMembers.length - 3}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white text-dark-blue text-xs font-primary font-semibold px-2 py-1 rounded-md shadow-[0_4px_12px_rgba(0,0,0,0.15)] whitespace-nowrap pointer-events-none opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 ease-out z-50">
                  {teamMembers.slice(3).map((m) => (
                    <div key={m.id}>
                      {m.first_name} {m.last_name}
                    </div>
                  ))}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-white" />
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  },
);

export default DeptTeamSelector;
